import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Navbar, Nav, NavDropdown, Offcanvas } from 'react-bootstrap';
import '../styles/layout.css';
import { BiUserCircle, BiBell, BiLogOut, BiHome } from 'react-icons/bi';
import { LiaUserEditSolid } from 'react-icons/lia';
import Cookies from 'universal-cookie';
import { FaBars } from 'react-icons/fa';

import AppLogo from './AppLogo';
import { logout as partnerLogout } from '../store/features/partnerSlice';
import { logout as supportLogout, resetSupportAuth } from '../store/features/supportSlice';
import { logout as fieldOfficerLogout, resetFieldOfficerAuth } from '../store/features/fieldOfficersSlice';
import { logout as farmerLogout, resetFarmerAuth } from '../store/features/farmerSlice';
import { clearRole, selectCurrentRole } from '../store/features/roleSlice';
import { useSidebar } from '../contexts/SidebarContext';
import axiosInstance from '../utils/axios';

function Header() {
  const dispatch = useDispatch();
  const cookies = new Cookies();
  
  const role = useSelector(selectCurrentRole); 
  const { toggle } = useSidebar(); // Get toggle function from context

  const [notificationWindow, setNotificationWindow] = useState(false);
  
  const { support } = useSelector((state) => state.support)
  const { partner } = useSelector((state) => state.partner);
  const { fieldOfficerInfo } = useSelector((state) => state.fieldOfficer);
  const { farmerInfo } = useSelector((state) => state.farmer);

  const handleLogout = async () => {
    try {
      if (role === 'SUPPORT') {
        await dispatch(supportLogout()).unwrap();
      } else if (role === 'PARTNER' || role === 'TEAM_MEMBER') {
        await dispatch(partnerLogout());
      } else if (role === 'FIELD_OFFICER') {
        await dispatch(fieldOfficerLogout()).unwrap();
      } else if (role === 'FARMER') {
        await dispatch(farmerLogout()).unwrap();
      }
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      dispatch(resetFieldOfficerAuth());
      dispatch(resetSupportAuth());
      dispatch(resetFarmerAuth());
      dispatch(clearRole());
      localStorage.clear();
      const allCookies = cookies.getAll();
      Object.keys(allCookies).forEach((cookieName) => {
        cookies.remove(cookieName, { path: '/' });
      });

      try {
        await axiosInstance.get('/reset');
      } catch (resetErr) {
        console.error('Reset API failed, but continuing logout...', resetErr);
      }

      window.location.href = '/login';
    }
  };

  const renderUserDetails = () => {
    switch (role) {
      case 'PARTNER':
        return (
          <>
            <span className="fw-bold">{partner?.primary_person_name}</span>
            <br />
            <small className="text-muted">+91 {partner?.primary_person_mobile_no}</small>
          </>
        );
      case 'FIELD_OFFICER':
        return (
          <>
            <span className="fw-bold">
              {fieldOfficerInfo?.name
                ? fieldOfficerInfo.name.charAt(0).toUpperCase() + fieldOfficerInfo.name.slice(1)
                : 'Field Officer'}
            </span>
            <br />
            <small className="text-muted">{fieldOfficerInfo?.mobile_no}</small>
          </>
        );
      case 'FARMER':
        return (
          <>
            <span className="fw-bold">
              {farmerInfo?.first_name
                ? `${farmerInfo.first_name}`
                : 'Farmer'}
            </span>
            <br />
            <small className="text-muted">{farmerInfo?.mobile_no || 'Registered User'}</small>
          </>
        );
      case 'SUPPORT':
        return (
          <>
            <span className="fw-bold">Hello {support?.name}</span>
          </>
        );
        default:
          return null;
    }
  };

  return (
    <Navbar bg="light" data-bs-theme="light" expand="lg" className="shadow-sm">
      <Container fluid className="px-md-4 px-3">
        {/* Hamburger Menu Button - Only visible on mobile */}
        <button
          className="btn d-md-none me-2"
          onClick={toggle}
          style={{ border: 'none', background: 'transparent', padding: '4px' }}
          aria-label="Toggle sidebar"
        >
          <FaBars size={24} className="text-black" />
        </button>

        <Navbar.Brand as={Link} to="/">
          <AppLogo
            partnerLogoUrl={role === 'PARTNER' ? partner?.profile_picture : null}
            alt="Soil Doctor Logo"
            style={{
              width: '220px',
              maxWidth: 'calc(100vw - 100px)',
              height: '60px',
              maxHeight: '60px'
            }}
          />
        </Navbar.Brand>

        <Nav className="justify-content-end flex-row gap-2">
           <Nav.Link onClick={() => setNotificationWindow(true)} className="d-flex align-items-center">
            <div className="position-relative">
              <BiBell size={28} className="text-black" />
            </div>
          </Nav.Link>

          <NavDropdown
            title={<BiUserCircle size={'30px'} className="text-black" />}
            id='collasible-nav-dropdown'
            align="end"
            menuVariant="light"
            className="user-dropdown"
          >
            <NavDropdown.Item className="text-black user-details-item" style={{ cursor: 'default' }}>
              {renderUserDetails()}
            </NavDropdown.Item>

            <NavDropdown.Divider />

            <NavDropdown.Item 
            as={Link} 
            to="/" 
            className="d-flex align-items-center gap-2"
            >
              <BiHome /> Home
            </NavDropdown.Item>

            {(role === 'PARTNER' || role === 'TEAM_MEMBER' ) && (
              <NavDropdown.Item 
              as={Link} 
              to="/partner/profile" 
              className="d-flex align-items-center gap-2"
              >
                <LiaUserEditSolid /> Profile
              </NavDropdown.Item>
            )}

            {(role === 'FIELD_OFFICER' || role === 'FARMER') && (
              <NavDropdown.Item 
              as={Link} 
              to="/profile" 
              className="d-flex align-items-center gap-2"
              >
                <LiaUserEditSolid /> My Profile
              </NavDropdown.Item>
            )}

            {(role === 'SUPPORT') && (
              <NavDropdown.Item 
              as={Link} 
              to="/operations/profile" 
              className="d-flex align-items-center gap-2"
              >
                <LiaUserEditSolid /> My Profile
              </NavDropdown.Item>
            )}

            <NavDropdown.Divider />

            <NavDropdown.Item 
            className="text-danger d-flex align-items-center gap-2" 
            onClick={handleLogout}
            >
              <BiLogOut transform="rotate(180)" /> Logout
            </NavDropdown.Item>
          </NavDropdown>
        </Nav>

         <Offcanvas 
         show={notificationWindow} 
         onHide={() => setNotificationWindow(false)} 
         placement="end"
         backdrop ="static"
         >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              <h5 className="fw-bold mb-0">Notifications</h5>
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <div className="notification-item">
              <div className="icon">
                <BiUserCircle />
              </div>
              <div className="content">
                <div className="text">Report Created! Check it here</div>
                <div className="time">2 hours ago</div>
              </div>
            </div>

            <div className="notification-item alternate">
              <div className="icon">
                <BiUserCircle />
              </div>
              <div className="content">
                <div className="text">Sample Testing in Progress</div>
                <div className="time">3 hours ago</div>
              </div>
            </div>

            <div className="notification-item">
              <div className="icon">
                <BiUserCircle />
              </div>
              <div className="content">
                <div className="text">Verify your Email</div>
                <div className="time">5 hours ago</div>
              </div>
            </div>
          </Offcanvas.Body>
         </Offcanvas>
      </Container>
    </Navbar>
  );
}

export default Header;